// src/pages/gene.codes/bloods/BloodAnalysiscomponents/BloodResultsOverview.tsx
import React, { useMemo, useState, useEffect } from "react";
import { Button, Typography } from "antd";
import { ArrowRightOutlined, BarChartOutlined, ExclamationCircleOutlined, KeyOutlined } from "@ant-design/icons";

import { SectionCard, StatusTag } from "../../../../components/shared";



import BloodTestWheel from "./BloodTestWheel";
import ResultRangeBar from "./ResultRangeBar";
import BloodAnalysisStateNotice from "./BloodAnalysisStateNotice";

import { BloodTest, BloodTestResult, BloodTestAnalysis } from "../../../../types/lab/BloodTestTypes";
import { BloodTestSubCategory } from "../../../../types/lab/BloodTestCatalogueTypes";
import { buildBloodWheelData, BloodWheelCategory } from "../../../../utils/geneCodes/buildBloodWheelData";
import { calculateResultSeverity } from "../../../../utils/geneCodes/bloodResultUtils";
import { RangeContext } from "../../../../utils/geneCodes/referenceRange";
const { Text } = Typography;
interface BloodResultsOverviewProps {
	bloodTest: BloodTest;
	analysis: BloodTestAnalysis | null;
	catalogue: BloodTestSubCategory[];
	rangeContext: RangeContext;
	onCategoryClick: (category: string) => void;
	onResultClick?: (result: BloodTestResult) => void;
	selectedResult?: BloodTestResult | null;
}

const BloodResultsOverview: React.FC<BloodResultsOverviewProps> = ({ bloodTest, analysis, catalogue, rangeContext, onCategoryClick, onResultClick, selectedResult }) => {
	const [isMobile, setIsMobile] = useState(false);
	const [wheelSize, setWheelSize] = useState({ width: 450, height: 450 });
	// Number of columns for the Priority Results grid — scales with screen width
	// so as many results as possible stay visible at the top.
	const [priorityCols, setPriorityCols] = useState(3);

	useEffect(() => {
		const checkMobile = () => {
			const w = window.innerWidth;
			const mobile = w < 768;
			setIsMobile(mobile);
			setPriorityCols(w < 768 ? 1 : w < 1280 ? 2 : 3);

			if (mobile) {
				const size = Math.min(window.innerWidth - 60, 350);
				setWheelSize({ width: size, height: size });
			} else {
				setWheelSize({ width: 450, height: 450 });
			}
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	// Catalogue-driven buckets for the wheels; an unavailable/empty catalogue falls back to
	// test_category grouping so the wheels still render and nothing is dropped.
	const categorizedResults = useMemo<BloodWheelCategory[]>(() => {
		const results = bloodTest.results ?? [];
		if (results.length === 0) return [];

		if (catalogue && catalogue.length > 0) {
			return buildBloodWheelData(results, catalogue);
		}

		// Fallback: group by the analyte's existing test_category.
		const fallback = new Map<string, BloodTestResult[]>();
		results.forEach((result) => {
			const category = result.test_category || "Other / Ungrouped";
			if (!fallback.has(category)) fallback.set(category, []);
			fallback.get(category)!.push(result);
		});

		return Array.from(fallback.entries())
			.map(([name, bucket]) => ({
				name,
				color: null,
				sort_order: name === "Other / Ungrouped" ? Number.MAX_SAFE_INTEGER : 0,
				results: [...bucket].sort((a, b) => a.sort_order - b.sort_order),
			}))
			.sort((a, b) => {
				if (a.name === "Other / Ungrouped") return 1;
				if (b.name === "Other / Ungrouped") return -1;
				return a.name.localeCompare(b.name);
			});
	}, [bloodTest.results, catalogue]);

	const getRiskStatus = (risk: string) => {
		switch (risk?.toLowerCase()) {
			case "high": return "failed";
			case "moderate": return "waiting";
			case "low": return "completed";
			default: return "info";
		}
	};

	// Derived flags (RR-1), never the lab's printed flag
	const topAbnormalResults = useMemo(() => {
		if (!bloodTest.results) return [];
		const flagRank = (flag: string | null) => (flag === "C" ? 3 : flag === "H" || flag === "L" ? 2 : 1);
		return bloodTest.results
			.map((result) => ({ result, flag: calculateResultSeverity(result, rangeContext).flag }))
			.filter(({ flag }) => flag !== null)
			.sort((a, b) => flagRank(b.flag) - flagRank(a.flag))
			.map(({ result }) => result)
			.slice(0, 5);
	}, [bloodTest.results, rangeContext]);

	const renderHealthOverview = () => {
		if (!analysis || analysis.status !== "completed") {
			return (
				<SectionCard style={{ marginBottom: 16 }}>
					<BloodAnalysisStateNotice analysis={analysis} />
				</SectionCard>
			);
		}

		return (
			<SectionCard style={{ marginBottom: 16 }}>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{analysis.health_summary && (
						<div style={{ padding: isMobile ? 12 : 16, borderRadius: 6, border: "1px solid var(--mg-border)" }}>
							<Text style={{ display: "block", marginBottom: 8, fontSize: 12, fontWeight: 600 }}>Health Summary</Text>
							<Text type="secondary" style={{ fontSize: 12, lineHeight: "1.6", display: "block" }}>{analysis.health_summary}</Text>
						</div>
					)}

					<div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
						{analysis.cardiovascular_risk && (
							<div style={{ padding: isMobile ? 8 : 12, borderRadius: 6, textAlign: "center", border: "1px solid var(--mg-border)" }}>
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>CARDIOVASCULAR</Text>
								<StatusTag status={getRiskStatus(analysis.cardiovascular_risk)} label={analysis.cardiovascular_risk} size="small" />
							</div>
						)}
						{analysis.diabetes_risk && (
							<div style={{ padding: isMobile ? 8 : 12, borderRadius: 6, textAlign: "center", border: "1px solid var(--mg-border)" }}>
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>DIABETES</Text>
								<StatusTag status={getRiskStatus(analysis.diabetes_risk)} label={analysis.diabetes_risk} size="small" />
							</div>
						)}
						{analysis.kidney_disease_risk && (
							<div style={{ padding: isMobile ? 8 : 12, borderRadius: 6, textAlign: "center", border: "1px solid var(--mg-border)" }}>
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>KIDNEY</Text>
								<StatusTag status={getRiskStatus(analysis.kidney_disease_risk)} label={analysis.kidney_disease_risk} size="small" />
							</div>
						)}
						{analysis.liver_disease_risk && (
							<div style={{ padding: isMobile ? 8 : 12, borderRadius: 6, textAlign: "center", border: "1px solid var(--mg-border)" }}>
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>LIVER</Text>
								<StatusTag status={getRiskStatus(analysis.liver_disease_risk)} label={analysis.liver_disease_risk} size="small" />
							</div>
						)}
					</div>
				</div>
			</SectionCard>
		);
	};

	const renderBloodTestWheels = () => {
		return (
			<div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(400px, 1fr))", gap: "16px" }}>
				{categorizedResults.map((category) => {
					return (
						<SectionCard key={category.name} style={{ cursor: "pointer", transition: "all 0.2s" }}>
							<div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
								{category.color && (
									<div style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
										<span style={{ width: 10, height: 10, borderRadius: "50%", background: category.color, display: "inline-block" }} />
										<Text style={{ fontSize: 12, fontWeight: 600 }}>{category.name}</Text>
									</div>
								)}

								<BloodTestWheel results={category.results} category={category.name} rangeContext={rangeContext} onResultClick={onResultClick || (() => {})} selectedResult={selectedResult || null} width={wheelSize.width} height={wheelSize.height} />

								<div style={{ width: "100%", marginTop: 12 }}>
									<Button icon={<ArrowRightOutlined />} style={{ width: "100%" }} size="small" onClick={() => onCategoryClick(category.name)}>
										View Details
									</Button>
								</div>
							</div>
						</SectionCard>
					);
				})}
			</div>
		);
	};

	const renderAbnormalResults = () => {
		if (topAbnormalResults.length === 0) return null;

		return (
			<SectionCard
				title={
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<ExclamationCircleOutlined />
						<Text style={{ fontSize: 12, fontWeight: 600 }}>Priority Results</Text>
					</div>
				}
				extra={<Text type="secondary" style={{ fontSize: 11 }}>{topAbnormalResults.length}</Text>}
				style={{ marginBottom: 16 }}
			>
				<div style={{ display: "grid", gridTemplateColumns: `repeat(${priorityCols}, 1fr)`, gap: 8 }}>
					{topAbnormalResults.map((result) => (
						<div
							key={result.id}
							style={{
								padding: 8,
								border: "1px solid var(--mg-border)",
								borderRadius: 4,
								cursor: "pointer",
								transition: "all 0.2s",
							}}
							onClick={() => onResultClick && onResultClick(result)}
						>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 6 }}>
								<Text style={{ fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{result.test_name}</Text>
								<Text style={{ fontSize: 11, opacity: 0.8, whiteSpace: "nowrap" }}>{result.result_value} {result.units}</Text>
							</div>
							<ResultRangeBar result={result} rangeContext={rangeContext} compact={true} showLabels={false} />
						</div>
					))}
				</div>
			</SectionCard>
		);
	};

	const renderKeyFindings = () => {
		if (!analysis?.key_findings) return null;

		return (
			<SectionCard
				title={
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<KeyOutlined />
						<span>Key Findings</span>
					</div>
				}
				style={{ marginBottom: 16 }}
			>
				<div style={{ lineHeight: 1.6, fontSize: 12 }} dangerouslySetInnerHTML={{ __html: analysis.key_findings }} />
			</SectionCard>
		);
	};

	return (
		<div>
			{renderHealthOverview()}
			{renderKeyFindings()}
			{renderAbnormalResults()}

			<div style={{ marginBottom: 16 }}>
				<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
					<BarChartOutlined style={{ color: "var(--mg-primary)" }} />
					<Text style={{ fontSize: 13, fontWeight: 600 }}>Test Categories</Text>
				</div>
				{renderBloodTestWheels()}
			</div>
		</div>
	);
};

export default BloodResultsOverview;
